<template>
    <!-- One route for everyone; admin and standard users get different settings. Only the admin side exists so far
         (the nav link is admin-only). The "Notify when" options come later. -->
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Notifications</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <settings-layout v-if="isAdmin" desc="Choose what's sent and who it goes to.">
                <div class="form-wrapper settings-form">
                    <!-- The signed-in admin's own alerts: API users.ts /me/notifications, adminNotifications.ts -->
                    <h2 class="settings-section-h">Your Alerts</h2>
                    <section class="settings-card">
                        <table v-if="alerts.length" class="ntf-table">
                            <thead><tr><th>Notify me when…</th><th>Email</th><th>Text</th></tr></thead>
                            <tbody>
                                <tr v-for="alert in alerts" :key="alert.key">
                                    <td>
                                        {{ alert.label }}<span v-if="alert.hint" class="item-hint ntf-alert-hint">{{ alert.hint }}</span>
                                        <!-- Only for some locations (accessRequest); none stored = every location -->
                                        <div v-if="alert.byLocation && isOn(alert) && $store.state.user.locations?.length > 1" class="chip-editor-item-opts thl-locs ntf-locs">
                                            <label v-for="loc in $store.state.user.locations" :key="loc" class="checkbox-input">
                                                <input type="checkbox" :checked="hasLocation(alert, loc)" @change="toggleLocation(alert, loc)" />
                                                <span>{{ loc }}</span>
                                            </label>
                                        </div>
                                    </td>
                                    <td v-for="via in ['email', 'text']" :key="via">
                                        <label :class="['item-private', 'ntf-switch', { 'ntf-locked': locked(alert, via) }]" :title="locked(alert, via) ? 'At least one of email or text stays on for this one' : (via == 'email' ? 'Email' : 'Text') + ': ' + alert.label">
                                            <input type="checkbox" v-model="mine.notifications[alert.key][via]" :disabled="locked(alert, via)" />
                                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                                        </label>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3>Phone for Text Alerts</h3>
                        <phone-input v-model="mine.phone" />
                        <div class="chip-editor-hint ntf-phone-hint">
                            Your text alerts go to this number<span v-if="mine.email"> and email alerts to {{ mine.email }}</span>.
                            <span v-if="textsOn && !mine.phone" class="ntf-warn">Add a phone number to get your text alerts.</span>
                        </div>
                    </section>

                    <h2 class="settings-section-h">Company Emails &amp; Texts</h2>
                    <!-- Typed emails, since these can go to people outside the Ironbook (e.g. payroll) -->
                    <section v-if="isTeamHubAdmin" class="settings-card">
                        <email-recipients-editor v-model="formData.EmployeeEmailsNew" label="Team Hub Employee Email Recipients" hint="Notified of employee updates and terminations for the locations ticked, when Send Admin Email is checked on the Edit Employee form. They don't need to be Ironbook users." :show-locations="true" />
                    </section>

                    <!-- API cronjobs.ts dailySignReminderTexts; off by default (sql/company-sign-reminder-texts.sql) -->
                    <section class="settings-card">
                        <label class="item-private ntf-toggle">
                            <span class="item-private-text">
                                <span class="item-private-title">Sign-Off Reminder Texts</span>
                                <span class="item-hint">Text standard users once a day when they have new or updated processes to sign. Leave this off while your Ironbook is still being built out, so people aren't texted about every change.</span>
                            </span>
                            <input type="checkbox" v-model="formData.SignReminderTexts" />
                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                        </label>
                    </section>

                    <div class="btn-row settings-save">
                        <button type="button" class="btn-lg" :disabled="disableButton" @click="submit()">Save Changes</button>
                        <button type="button" class="btn-lg btn-light" :disabled="disableButton" @click="load()">Cancel</button>
                    </div>
                </div>
            </settings-layout>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import EmailRecipientsEditor from '@/components/EmailRecipientsEditor.vue';
import SettingsLayout from '@/components/SettingsLayout.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import { isValidPhone, normalizePhone } from '@/phone';
import { ADMIN_ALERTS } from '@/adminNotifications';

function parseList(json: any): any[] {
    try {
        return JSON.parse(json) || [];
    } catch(e) {
        return [];
    }
}

// Every alert's email/text choice: the saved ones, else each alert's default (so the table always has a row to bind).
// The API fills in the defaults too; these only show until it answers.
function alertChoices(saved: any): any {
    const choices: any = {};
    for(const alert of ADMIN_ALERTS) {
        choices[alert.key] = { email: alert.email, text: alert.text, ...(saved?.[alert.key] || {}) };
        if(alert.required && !choices[alert.key].email && !choices[alert.key].text) choices[alert.key].email = true;
    }
    return choices;
}

export default defineComponent({
    name: 'NotificationsView',
    components: {
        EmailRecipientsEditor,
        SettingsLayout,
        PhoneInput
    },
    data() {
        return {
            formData: {
                SignReminderTexts: false,
                EmployeeEmailsNew: [] as any[]
            },
            // The signed-in admin's own settings
            mine: {
                phone: '',
                email: '',
                notifications: alertChoices(null)
            },
            alerts: ADMIN_ALERTS,
            // What was last loaded or saved, to tell whether there are unsaved changes (see snapshot())
            savedSnapshot: '',
            disableButton: false
        }
    },
    computed: {
        isAdmin(): boolean {
            return this.$store.state.user.role == 'Admin';
        },
        isTeamHubAdmin(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin';
        },
        textsOn(): boolean {
            return this.alerts.some(alert => this.mine.notifications[alert.key]?.text);
        },
        dirty(): boolean {
            return !!this.savedSnapshot && this.snapshot() != this.savedSnapshot;
        }
    },
    async created() {
        if(this.isAdmin) {
            await this.load();
        }

        this.$store.state.routeLoaded = true;
    },
    mounted() {
        window.addEventListener('beforeunload', this.warnUnload);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnUnload);
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        if(this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
            next(false);
            return;
        }
        next();
    },
    methods: {
        // Everything Save Changes sends; the phone as it's stored, so the phone field reformatting it isn't a change
        snapshot(): string {
            return JSON.stringify({
                phone: normalizePhone(this.mine.phone) || this.mine.phone || '',
                notifications: this.mine.notifications,
                company: this.formData
            });
        },
        warnUnload(e: any) {
            if(this.dirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        },
        // A required alert's last switch that's on can't be turned off
        locked(alert: any, via: string): boolean {
            const choice = this.mine.notifications[alert.key];
            return !!alert.required && !!choice?.[via] && !choice[via == 'email' ? 'text' : 'email'];
        },
        isOn(alert: any): boolean {
            const choice = this.mine.notifications[alert.key];
            return !!(choice?.email || choice?.text);
        },
        hasLocation(alert: any, loc: string): boolean {
            const locations = this.mine.notifications[alert.key]?.locations;
            return !locations || locations.includes(loc);
        },
        // Every location ticked is stored as no list at all, so locations added later are included too
        toggleLocation(alert: any, loc: string) {
            const choice = this.mine.notifications[alert.key];
            const all: string[] = this.$store.state.user.locations || [];
            const current: string[] = choice.locations || all;
            const next = current.includes(loc) ? current.filter(l => l != loc) : [ ...current, loc ];
            if(all.every(l => next.includes(l))) delete choice.locations;
            else choice.locations = next;
        },
        async load() {
            await Promise.all([ this.getConfiguration(), this.getMine() ]);
            this.savedSnapshot = this.snapshot();
        },
        async getMine() {
            await axios.get(`${this.$store.state.apiUrl}/api/users/me/notifications`)
                .then(resp => {
                    this.mine = { phone: resp.data.phone || '', email: resp.data.email || '', notifications: alertChoices(resp.data.notifications) };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getConfiguration() {
            await axios.get(`${this.$store.state.apiUrl}/api/companyconfig`)
                .then(resp => {
                    const company = resp.data;
                    this.formData = {
                        SignReminderTexts: !!company.SignReminderTexts,
                        EmployeeEmailsNew: parseList(company.EmployeeEmailsJSON)
                    };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async submit() {
            if(this.mine.phone && !isValidPhone(this.mine.phone)) {
                alert('Enter a complete phone number for your text alerts, or leave it empty');
                return;
            }
            if(this.textsOn && !this.mine.phone) {
                alert('Add a phone number to get your text alerts, or turn them off');
                return;
            }
            const limited = this.alerts.find(a => a.byLocation && this.isOn(a) && this.mine.notifications[a.key].locations?.length === 0);
            if(limited) {
                alert(`Pick at least one location for "${limited.label}", or turn it off`);
                return;
            }

            this.disableButton = true;

            await Promise.all([
                axios.post(`${this.$store.state.apiUrl}/api/users/me/notifications`, {
                    phone: this.mine.phone,
                    notifications: this.mine.notifications
                }),
                axios.post(`${this.$store.state.apiUrl}/api/companyconfig/notifications`, {
                    EmployeeEmailsJSON: JSON.stringify(this.formData.EmployeeEmailsNew),
                    SignReminderTexts: this.formData.SignReminderTexts
                })
            ])
                .then(() => {
                    this.savedSnapshot = this.snapshot();
                    alert('Notifications have been updated!');
                })
                .catch(e => {
                    alert(typeof e?.response?.data == 'string' && e.response.data ? e.response.data : 'There was an error');
                    console.log(e);
                })

            this.disableButton = false;
        }
    }
});
</script>
